import { TextField } from '@mui/material'
import PropTypes from 'prop-types'
import { useFieldState } from 'hooks/useFieldState'
import { useFieldFocus } from 'context/FieldFocusContext'
import { useRef, useEffect } from 'react'

const styles = {
  '&>label': {
    fontSize: '14px',
    color: '#75787B',
    top: '-2px',
  },
  '&>label.Mui-focused': {
    fontSize: '1rem',
    color: '#2370ED',
    background: '#fff',
    padding: '0 5px',
    marginLeft: '-5px',
    top: 0,
  },
  '&>label.Mui-focused.Mui-error': {
    color: '#ea0606',
  },
  '&>label.MuiFormLabel-filled, &>label.MuiInputLabel-shrink': {
    fontSize: '1rem',
    background: '#fff',
    padding: '0 5px',
    marginLeft: '-5px',
    top: 0,
  },
  '&>label.MuiFormLabel-filled.Mui-disabled, &>label.MuiInputLabel-shrink.Mui-disabled':
    {
      top: 0,
      background:
        'linear-gradient(to bottom, #fff 0%, #fff 50%, #e9ecef 50%, #e9ecef 100%)',
    },
  '&>div.MuiInputBase-root': {
    padding: '0!important',
    borderRadius: '4px !important',
  },
  '&>div.MuiInputBase-root.Mui-disabled': {
    backgroundColor: '#e9ecef!important',
  },
  '&>div.MuiInputBase-root.Mui-focused': {
    boxShadow: '0 0 0 1.5px #2370ED',
  },
  '&>div>.MuiInputBase-input': {
    width: '100%!important',
    padding: '0.75rem 1.75rem 0.75rem 0.75rem!important',
    display: 'flex',
  },
  '&>div.MuiInputBase-root>svg': {
    display: 'block',
    height: '1.5em',
    width: '1.5em',
    top: 'calc(50% - 0.75em)',
  },
  '&.linkedDisabled>div.MuiInputBase-root.Mui-disabled': {
    backgroundColor: '#F5EBFE !important',
    border: '1px solid #8F62F8',
  },
  '&.linkedDisabled>label.MuiFormLabel-filled.Mui-disabled, &.linkedDisabled>label.MuiInputLabel-shrink.Mui-disabled':
    {
      background:
        'linear-gradient(to bottom, #fff 0%, #fff 50%, #F5EBFE 50%, #F5EBFE 100%)',
      color: '#8F62F8',
    },
  '&.linkedDisabled>div>.MuiInputBase-input': {
    color: '#8F62F8',
    WebkitTextFillColor: '#8F62F8',
  },
}

export const MuiDateField = ({
  value = '',
  onChange = () => {},
  onBlur = () => {},
  label = 'Date',
  required = false,
  id,
  name,
  enableHelpText,
  helpText,
  ...rest
}) => {
  const { isError, isSuccess, handleOnblur } = useFieldState({
    required,
    validatorFunction: (val) =>
      `${val}`.match(/^(?!0{1,3}\d)\d{4}-\d{2}-\d{2}$/),
  })

  const { handleFocus, handleBlur, registerHelpField } = useFieldFocus()
  const inputRef = useRef(null)
  const fieldId = id || name || label

  useEffect(() => {
    if (enableHelpText && fieldId && helpText) {
      registerHelpField(fieldId, helpText)
    }
  }, [enableHelpText, fieldId, helpText])

  const handleOnblurWithValidation = (e) => {
    let val = e.target.value
    const isValid = `${val}`.match(/^(?!0{1,3}\d)\d{4}-\d{2}-\d{2}$/)
    handleOnblur?.(val)
    if (isValid) {
      onBlur?.(val)
    }
    handleBlur()
  }

  return (
    <TextField
      fullWidth
      type="date"
      label={label}
      value={value}
      id={fieldId}
      name={fieldId}
      inputRef={inputRef}
      onChange={(e) => onChange(e.target.value)}
      onFocus={(e) => {
        handleFocus(fieldId, e.target)
      }}
      onBlur={handleOnblurWithValidation}
      onMouseEnter={(e) => handleFocus(fieldId, e.currentTarget)}
      onMouseLeave={(e) => {
        if (document.activeElement !== e.currentTarget) {
          handleBlur()
        }
      }}
      sx={styles}
      InputLabelProps={{ shrink: true }}
      required={required}
      error={isError}
      {...rest}
    />
  )
}

MuiDateField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  label: PropTypes.string,
  required: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  enableHelpText: PropTypes.bool,
  helpText: PropTypes.string,
}
